import { useEffect, useState, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import apiClient from '../clients/apiClient';

interface ScanResult {
    customer_id: number;
    customer_number: string;
    full_name: string;
    marked: boolean;
    already_present?: boolean;
    time: string;
}

export default function MemberMeetingScan() {
    const { instanceId } = useParams<{ instanceId: string }>();
    const [status, setStatus] = useState('Initializing camera...');
    const [statusType, setStatusType] = useState<'idle' | 'listening' | 'success' | 'error' | 'ended'>('idle');
    const [results, setResults] = useState<ScanResult[]>([]);
    const [manualInput, setManualInput] = useState('');
    const readerContainerRef = useRef<HTMLDivElement>(null);
    const scannerRef = useRef<any>(null);

    const setStatusMsg = useCallback((text: string, type: typeof statusType) => {
        setStatus(text);
        setStatusType(type);
    }, []);

    const addResult = useCallback((name: string, id: string, time: string, success: boolean) => {
        setResults((prev) => [{ customer_id: 0, customer_number: id, full_name: name, marked: success, time }, ...prev]);
    }, []);

    const submitScan = useCallback(async (identifier: string) => {
        if (!instanceId) return;
        setStatusMsg('Processing...', 'idle');
        try {
            const resp = await apiClient.post(`/member-meetings/instances/${instanceId}/scan`, { identifier });
            const d = resp.data?.data;
            if (d) {
                const time = new Date().toLocaleTimeString();
                addResult(d.full_name || d.customer_number, d.customer_number, time, true);
                setStatusMsg(`Marked: ${d.full_name || d.customer_number}`, 'success');
                setTimeout(() => setStatusMsg('Scan next card...', 'listening'), 2000);
            } else {
                throw new Error(resp.data?.detail || 'Not found');
            }
        } catch (err: any) {
            const detail = err?.response?.data?.detail || 'Member not found or not an invitee';
            addResult(detail, identifier, new Date().toLocaleTimeString(), false);
            setStatusMsg(detail, 'error');
            setTimeout(() => setStatusMsg('Scan next card...', 'listening'), 2000);
        }
    }, [instanceId, setStatusMsg, addResult]);

    const submitManual = useCallback(() => {
        const val = manualInput.trim();
        if (val) {
            submitScan(val);
            setManualInput('');
        }
    }, [manualInput, submitScan]);

    useEffect(() => {
        if (!instanceId) {
            setStatusMsg('Invalid scan link', 'error');
            return;
        }

        let destroyed = false;

        const initScanner = async () => {
            // Load html5-qrcode library
            if (!(window as any).Html5Qrcode) {
                await new Promise<void>((resolve, reject) => {
                    const script = document.createElement('script');
                    script.src = 'https://unpkg.com/html5-qrcode@2.3.8/html5-qrcode.min.js';
                    script.onload = () => resolve();
                    script.onerror = () => reject(new Error('Failed to load QR scanner'));
                    document.head.appendChild(script);
                });
            }

            if (destroyed) return;

            try {
                const Html5Qrcode = (window as any).Html5Qrcode;
                const scanner = new Html5Qrcode('qr-reader');
                scannerRef.current = scanner;

                await scanner.start(
                    { facingMode: 'environment' },
                    { fps: 10, qrbox: { width: 250, height: 250 }, aspectRatio: 1.0 },
                    (decodedText: string) => {
                        if (!destroyed) submitScan(decodedText);
                    },
                    () => {}
                );

                if (!destroyed) {
                    setStatusMsg('Scan QR code or enter card number below', 'listening');
                }
            } catch (err: any) {
                if (!destroyed) {
                    setStatusMsg('Camera not available. Use manual input.', 'error');
                }
            }
        };

        initScanner();

        return () => {
            destroyed = true;
            if (scannerRef.current) {
                try {
                    scannerRef.current.stop().catch(() => {});
                    scannerRef.current.clear();
                } catch {}
                scannerRef.current = null;
            }
        };
    }, [instanceId, submitScan, setStatusMsg]);

    const statusClass: Record<string, string> = {
        idle: '#e8f0fe',
        listening: '#e6f4ea',
        success: '#e6f4ea',
        error: '#fce8e6',
        ended: '#fce8e6',
    };
    const statusColor: Record<string, string> = {
        idle: '#1a73e8',
        listening: '#137333',
        success: '#137333',
        error: '#d93025',
        ended: '#d93025',
    };

    return (
        <div style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', background: '#f0f2f5', minHeight: '100vh' }}>
            <div style={{ background: '#1a73e8', color: 'white', padding: '16px 20px', textAlign: 'center' }}>
                <h1 style={{ fontSize: '18px', fontWeight: 600, margin: 0 }}>Meeting Scanner</h1>
            </div>
            <div style={{ padding: '16px', maxWidth: '500px', margin: '0 auto' }}>
                <div style={{ background: 'white', borderRadius: '16px', padding: '24px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', marginBottom: '16px', textAlign: 'center' }}>
                    <div
                        style={{
                            padding: '12px',
                            borderRadius: '8px',
                            marginBottom: '12px',
                            fontSize: '14px',
                            fontWeight: 500,
                            background: statusClass[statusType] || '#e8f0fe',
                            color: statusColor[statusType] || '#1a73e8',
                        }}
                    >
                        {status}
                    </div>
                    <div id="qr-reader" ref={readerContainerRef} style={{ width: '100%', borderRadius: '12px', overflow: 'hidden' }} />
                    <div style={{ display: 'flex', gap: '8px', marginTop: '16px' }}>
                        <input
                            type="text"
                            value={manualInput}
                            onChange={(e) => setManualInput(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') submitManual(); }}
                            placeholder="Enter card number manually..."
                            style={{ flex: 1, padding: '10px 14px', border: '1px solid #dadce0', borderRadius: '8px', fontSize: '14px', outline: 'none' }}
                        />
                        <button
                            onClick={submitManual}
                            style={{ padding: '10px 20px', background: '#1a73e8', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', cursor: 'pointer', fontWeight: 500 }}
                        >
                            Submit
                        </button>
                    </div>
                </div>
                <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                    {results.map((r, i) => (
                        <div
                            key={i}
                            style={{
                                background: 'white',
                                borderRadius: '12px',
                                padding: '16px',
                                marginBottom: '8px',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                                borderLeft: r.marked ? '4px solid #137333' : '4px solid #d93025',
                            }}
                        >
                            <div style={{ fontWeight: 600, fontSize: '16px' }}>{r.full_name}</div>
                            <div style={{ color: '#666', fontSize: '13px' }}>{r.customer_number}</div>
                            <div style={{ color: '#999', fontSize: '12px', marginTop: '4px' }}>{r.time}</div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
